/**
 * Shared tracking helpers for Lakewood Med Spa.
 * Google Ads click conversions are handled by the capture-phase links in index.html;
 * these helpers provide app telemetry plus dependable Meta browser beacons.
 */

import { isLakewoodMetaProductionHost } from "@shared/metaTrackingPolicy";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    fbq: (...args: unknown[]) => void;
    oaiq: (...args: unknown[]) => void;
    umami?: { track: (eventName: string, data?: Record<string, unknown>) => void };
    gtag?: (...args: unknown[]) => void;
    lkwTrackMeta?: (eventName: string, parameters?: Record<string, unknown>, eventId?: string) => void;
    __lkwMetaBeacons?: HTMLImageElement[];
    __lkwMetaTrackingAllowed?: boolean;
  }
}

function genEventId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function push(event: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

/** Navigation-safe Meta event delivery used across all React pages. */
export function trackMetaEvent(
  eventName: string,
  parameters: Record<string, unknown> = {},
  eventId = genEventId(eventName.toLowerCase()),
) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isLakewoodMetaProductionHost(window.location.hostname)) return;

  const query = new URLSearchParams({
    id: "812839225030646",
    ev: eventName,
    dl: window.location.href,
    ts: String(Date.now()),
    if: "false",
    eid: eventId,
  });
  Object.entries(parameters).forEach(([key, value]) => {
    query.set(`cd[${key}]`, String(value));
  });

  const beacon = new Image(1, 1);
  beacon.alt = "";
  beacon.setAttribute("aria-hidden", "true");
  beacon.style.cssText = "position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;";
  beacon.src = `https://www.facebook.com/tr/?${query.toString()}`;
  window.__lkwMetaBeacons = window.__lkwMetaBeacons || [];
  window.__lkwMetaBeacons.push(beacon);
  (document.body || document.head || document.documentElement).appendChild(beacon);
}

if (typeof window !== "undefined") {
  window.lkwTrackMeta = trackMetaEvent;
}

/**
 * No monetary value on any of these.
 *
 * A tap on a phone number is not a call, a page view is not intent, and a click
 * through to the booking site is not a booking. Attaching an invented price to
 * one teaches value-based bidding that the cheap action is the valuable one,
 * and the optimiser obliges by finding more of it. The only honest money signal
 * is Booking Complete, which fires on the medsparecord confirmation page with a
 * value derived from the real cart.
 */
export function trackPhoneClick(source?: string) {
  push({ event: "phone_click", event_category: "Contact", event_label: source ?? "site" });
  if (typeof window !== "undefined" && typeof window.oaiq === "function") {
    // A tap is contact intent, not a submitted lead, and carries no amount.
    window.oaiq("measure", "contact_intent", { type: "contents" });
  }
}

export function trackFormSubmit(formName?: string) {
  const name = formName ?? "contact_form";
  push({ event: "form_submit", event_category: "Lead", event_label: name });
  trackMetaEvent("Lead", { content_name: name }, genEventId("lead_form"));
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "generate_lead", { form_name: name });
  }
  if (typeof window !== "undefined" && typeof window.oaiq === "function") {
    window.oaiq("measure", "lead_form_submitted", { type: "contents" });
  }
}

export function trackBookingPageView() {
  push({ event: "booking_page_view", event_category: "Booking", event_label: "booking_intent" });
  trackMetaEvent("Lead", { content_name: "booking_intent" }, genEventId("lead_booking"));
  if (typeof window !== "undefined" && typeof window.oaiq === "function") {
    window.oaiq("measure", "page_view", { type: "contents" });
  }
}

export function trackBookingClick(source?: string) {
  push({ event: "booking_click", event_category: "Booking", event_label: source ?? "cta" });
  if (typeof window !== "undefined" && window.umami?.track) {
    window.umami.track("book_appointment", { source: source ?? "cta" });
  }
  if (typeof window !== "undefined" && typeof window.oaiq === "function") {
    // A click is not an order. No order_created, no amount.
    window.oaiq("measure", "booking_intent", { type: "contents" });
  }
}

export function trackViewContent(contentName: string, contentCategory?: string) {
  const category = contentCategory ?? "Service";
  push({ event: "view_content", content_name: contentName, content_category: category });
  trackMetaEvent("ViewContent", { content_name: contentName, content_category: category }, genEventId("view_content"));
}
